/**
 * Générateur — Automatisme : connaitre et utiliser 1 × x = x ; x + x = 2x ;
 * x × x = x² ; 3x + 2x = 5x ; 3x × 2x = 6x².
 * Attendu ID : 1109 | Niveau : 4ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 10, ligne 709 :
 * « Connaitre et utiliser : 1 × x = x ; x + x = 2x ; x × x = x² ; 3x + 2x =
 * 5x ; 3x × 2x = 6x². »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Les CINQ règles, lecture stricte : coefficient 1 invisible ; x + x ; x × x ;
 * somme de deux monômes de degré 1 ; produit de deux monômes de degré 1.
 * Trois termes, x², constante, coefficient décimal, signe moins (5x − 2x) =
 * objectifs l. 613 et mise en œuvre p. 18 (gen_159/937) : exclus (À
 * ARBITRER pour 5x − 2x). 1109 est à 937 ce que 1058 est aux tables : la
 * règle sans la justification (a + b)x.
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NE11 = 3x × 2x
 * (même entrée que 1108) ; rien sur x + x ni 3x + 2x (4NE1 = objectif). Le
 * QCM « 3x + 2x » est l'item DEPP 4e 2023 q.6 tel quel.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX,
 *     un nombre à saisir (le coefficient ou l'exposant, la lettre restant
 *     écrite, convention de 937) : « 3x + 2x = …x » ; « 3x × 2x = …x² » ;
 *     « 5x + x = …x » ou « x + x = …x » ; « x × x = x^… » ou « 4x × x = …x² ».
 *   Carte 2 « Réduire » (reduire_qcm_unique) : « 3x + 2x, c'est… » QUATRE
 *     écritures 5x (clé), 5x² (carre_de_trop, LA principale : DEPP q.6,
 *     25,9 %), 6x² (somme_produit, 2,4 %), 6x (coefficients_multiplies,
 *     3,5 %) ; « 3x × 2x » : 6x², 5x² (coefficients_additionnes), 6x
 *     (x_oublie), 5x (somme_produit) ; « x × x » : x², 2x (double_pour_carre),
 *     x (trois choix) ; « x + x » : 2x, x², x, 2 (x_oublie). La clé est une
 *     écriture, pas une position : choix mélangés.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes mêlant les
 *     cinq règles (vraies et fausses), chaque ligne étiquetée par sa VALEUR
 *     pour trois valeurs de x (assert), une à trois vraies.
 *
 * INVARIANTS : coefficients 2..9 (produit ≤ 81) ; lettres x, n, t, y ;
 * double_pour_carre n'est servi que si x ≥ 3 au test (pour x = 2, 2x = x²).
 * SACRIFIÉ (fiche d'aide à venir, deux pages : rectangles 3x et 2x alignés
 * « 3x + 2x = 5x », rectangle 3x sur 2x quadrillé « 3x × 2x = 6x² ») : le
 * couple (3 ; 2), l'exemple du programme, dans les deux ordres, en somme et
 * en produit, auto-testé (les trois règles sans coefficient restent).
 *
 * ── Erreurs (familles nommées, DEPP 4e 2023 q.6) ─────────────────────────
 *   carre_de_trop            : 3x + 2x → 5x² (25,9 %), PRINCIPALE ;
 *   somme_produit            : 3x + 2x → 6x² (2,4 %) ; 3x × 2x → 5x ;
 *   coefficients_multiplies  : 3x + 2x → 6x (3,5 %) ;
 *   coefficients_additionnes : 3x × 2x → 5x² ;
 *   x_oublie                 : 3x × 2x → 6x ; x + x → 2 ;
 *   double_pour_carre        : x × x → 2x (base_fois_exposant de 1101, vue
 *                              depuis la lettre : même conception).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1109;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Réduire';
const L_VF = 'Vrai ou faux';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche : 3 et 2, sacrifiés dans les deux ordres, en somme et en produit. */
export const COUPLE_SACRIFIE = { a: 3, b: 2 };
const estSacrifie = (a, b) => (a === COUPLE_SACRIFIE.a && b === COUPLE_SACRIFIE.b) || (a === COUPLE_SACRIFIE.b && b === COUPLE_SACRIFIE.a);

const tirerCouple = () => {
    for (let essai = 0; essai < 60; essai++) {
        const a = randInt(2, 9);
        const b = randInt(2, 9);
        if (a === b || estSacrifie(a, b)) continue;
        return { a, b };
    }
    return { a: 4, b: 5 };
};

/**
 * Un monôme { coef, deg } ; une expression = liste de monômes. Écriture
 * KaTeX : « 3x », « x », « 6x^2 », « 2 ». Valeur pour x = v : le second
 * chemin de l'assert.
 */
const monomeTex = ({ coef, deg }, L) => `${coef === 1 && deg > 0 ? '' : coef}${deg === 0 ? '' : L}${deg === 2 ? '^2' : ''}`;
const valeur = (expr, v) => expr.reduce((s, { coef, deg }) => s + coef * v ** deg, 0);
const memeExpr = (e1, e2) => [2, 3, 5].every((v) => valeur(e1, v) === valeur(e2, v));

const EXPLICATIONS = {
    carre_de_trop: '3x + 2x, c\'est 3 x plus 2 x : 5 x, pas 5 x². Le carré vient d\'une multiplication de x par x.',
    somme_produit: 'On additionne les x, on ne les multiplie pas : 3x + 2x = 5x. Et 3x × 2x = 6x², pas 5x.',
    coefficients_multiplies: '3x + 2x : les coefficients s\'ajoutent, 3 + 2 = 5.',
    coefficients_additionnes: '3x × 2x : les coefficients se multiplient, 3 × 2 = 6, et x × x = x².',
    x_oublie: 'Le x ne disparaît pas : x + x = 2x, et 3x × 2x = 6x².',
    double_pour_carre: 'x × x = x², pas 2x : 2x c\'est x + x.',
    regle: '1 × x = x ; x + x = 2x ; x × x = x² ; 3x + 2x = 5x ; 3x × 2x = 6x².',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille du gen (catalogue validé des codes d'erreur). */
const CODE_COMMUN = {
    carre_de_trop: 'carre_de_trop', somme_produit: 'somme_produit_confondus', coefficients_multiplies: 'somme_produit_confondus',
    coefficients_additionnes: 'somme_produit_confondus', x_oublie: 'x_oublie', double_pour_carre: 'carre_lu_comme_double',
};
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : un code ne porte qu'une seule erreur. Sans nombre devant x
// (x + x, x × x), l'oubli de la lettre et le carré de trop ont leurs propres codes.
const SANS_COEFFICIENT = { x_oublie: 'lettre_oubliee_x_plus_x', carre_de_trop: 'carre_dans_x_plus_x' };
// Relecture du 04/10/2026 (E02) : « 2 » pour x + x oublie la lettre ; « x » pour x + x ou x × x la garde
// seule (la lettre absorbe, comme 1 × x = x) : deux erreurs, deux codes.
const codeDe = (famille, sansCoefficient, choix) => {
    if (sansCoefficient && famille === 'x_oublie') return choix === '2' ? 'lettre_oubliee_x_plus_x' : 'x_garde_seul';
    return (sansCoefficient && SANS_COEFFICIENT[famille]) || CODE_COMMUN[famille];
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre, son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** Série flash, ligne retrouvée par son écriture : les leurres du QCM jumeau et du reconnaisseur, carrés compris (5², 5n^2 pour 3n + 2n). */
const erreursFlash = (phrase, L, s, p, c) => {
    const carres = (k, code) => [`${k}²`, `${k}^2`, `${k}${L}²`, `${k}${L}^2`].map((valeur) => ({ code, valeur }));
    const PAR_LIGNE = [
        [`$${s.a}${L} + ${s.b}${L} = {{${s.a + s.b}}}${L}$`, s.a + s.b, [{ code: CODE_COMMUN.coefficients_multiplies, valeur: s.a * s.b }, ...carres(s.a + s.b, CODE_COMMUN.carre_de_trop), ...carres(s.a * s.b, CODE_COMMUN.somme_produit)]],
        [`$${p.a}${L} \\times ${p.b}${L} = {{${p.a * p.b}}}${L}^2$`, p.a * p.b, [{ code: CODE_COMMUN.double_pour_carre, valeur: 2 * p.a * p.b }, { code: CODE_COMMUN.coefficients_additionnes, valeur: p.a + p.b }]],
        [`$${c}${L} + ${L} = {{${c + 1}}}${L}$`, c + 1, [{ code: 'coefficient_implicite_oublie', valeur: c }, ...carres(c + 1, CODE_COMMUN.carre_de_trop), ...carres(c, CODE_COMMUN.somme_produit)]],
        [`$${L} + ${L} = {{2}}${L}$`, 2, [{ code: 'coefficient_implicite_oublie', valeur: 0 }, { code: CODE_COMMUN.x_oublie, valeur: 1 }]],
        [`$${L} \\times ${L} = ${L}^{ {{2}} }$`, 2, [{ code: CODE_COMMUN.x_oublie, valeur: 1 }]],
        // c² : l'élève met le coefficient au carré, (cn)² = c²n² ; cette histoire n'est pas nommée, et quand
        // elle donne la même valeur que 2c (c = 2 : 4), rien n'est nommé (relecture du prof, 03/10/2026).
        [`$${c}${L} \\times ${L} = {{${c}}}${L}^2$`, c, [{ code: CODE_COMMUN.double_pour_carre, valeur: 2 * c }, { code: CODE_COMMUN.coefficients_additionnes, valeur: c + 1 }, { code: null, valeur: c * c }]],
    ];
    return phrase.split('\n').flatMap((ligne, trou) => {
        const f = PAR_LIGNE.find(([l]) => l === ligne);
        return f ? nommer(f[2], f[1]).map(({ code, valeur }) => ({ code, trou, valeur })) : [];
    });
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const genFlash = () => {
    const L = randomChoice(LETTRES);
    const s = tirerCouple();
    const p = tirerCouple();
    const c = randInt(2, 9);
    const lignes = [
        `$${s.a}${L} + ${s.b}${L} = {{${s.a + s.b}}}${L}$`,
        `$${p.a}${L} \\times ${p.b}${L} = {{${p.a * p.b}}}${L}^2$`,
        randomChoice([true, false]) ? `$${c}${L} + ${L} = {{${c + 1}}}${L}$` : `$${L} + ${L} = {{2}}${L}$`,
        randomChoice([true, false]) ? `$${L} \\times ${L} = ${L}^{ {{2}} }$` : `$${c}${L} \\times ${L} = {{${c}}}${L}^2$`,
    ];
    const payload = makeTexteTrou(shuffle(lignes).join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    ranger(payload, erreursFlash(payload.contenu.phrase, L, s, p, c));
    payload._meta.params = { lettre: L, somme: s, produit: p, c };
    return payload;
};

// ─── Carte 2 : QCM « Réduire » ────────────────────────────────────────────

const genQcm = () => {
    const L = randomChoice(LETTRES);
    const regle = randomChoice(['somme', 'produit', 'somme', 'produit', 'x_fois_x', 'x_plus_x']);
    let enonce;
    let choix;
    let params;
    if (regle === 'somme') {
        const { a, b } = tirerCouple();
        enonce = `${a}${L} + ${b}${L}`;
        choix = [[`${a + b}${L}`, null], [`${a + b}${L}^2`, 'carre_de_trop'], [`${a * b}${L}^2`, 'somme_produit'], [`${a * b}${L}`, 'coefficients_multiplies']];
        params = { a, b };
    } else if (regle === 'produit') {
        const { a, b } = tirerCouple();
        enonce = `${a}${L} \\times ${b}${L}`;
        choix = [[`${a * b}${L}^2`, null], [`${a + b}${L}^2`, 'coefficients_additionnes'], [`${a * b}${L}`, 'x_oublie'], [`${a + b}${L}`, 'somme_produit']];
        params = { a, b };
    } else if (regle === 'x_fois_x') {
        enonce = `${L} \\times ${L}`;
        choix = [[`${L}^2`, null], [`2${L}`, 'double_pour_carre'], [L, 'x_oublie']];
        params = {};
    } else {
        enonce = `${L} + ${L}`;
        choix = [[`2${L}`, null], [`${L}^2`, 'carre_de_trop'], [L, 'x_oublie'], ['2', 'x_oublie']];
        params = {};
    }
    const textes = choix.map(([t]) => tex(t));
    const payload = makeQcm(textes, textes[0], `<p><b>${tex(enonce)}, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, nommer(choix.slice(1).map(([t, code], i) => ({ code: codeDe(code, regle === 'x_plus_x' || regle === 'x_fois_x', t), valeur: textes[i + 1] })), textes[0]));
    payload._meta.params = { lettre: L, regle, ...params, distracteurs: choix.slice(1).map(([, code]) => ({ code })) };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (4 lignes, les cinq règles) ───────────────────

/** Gabarits : { gauche, droite } en monômes, vrai/faux vérifié en valeur. */
const GABARITS = {
    un_fois_x: (L) => ({ gauche: `1 \\times ${L}`, g: [{ coef: 1, deg: 1 }], droite: L, d: [{ coef: 1, deg: 1 }], vrai: true }),
    x_plus_x: (L) => ({ gauche: `${L} + ${L}`, g: [{ coef: 2, deg: 1 }], droite: `2${L}`, d: [{ coef: 2, deg: 1 }], vrai: true }),
    x_plus_x_faux: (L) => ({ gauche: `${L} + ${L}`, g: [{ coef: 2, deg: 1 }], droite: `${L}^2`, d: [{ coef: 1, deg: 2 }], vrai: false, code: 'carre_de_trop' }),
    x_fois_x: (L) => ({ gauche: `${L} \\times ${L}`, g: [{ coef: 1, deg: 2 }], droite: `${L}^2`, d: [{ coef: 1, deg: 2 }], vrai: true }),
    x_fois_x_faux: (L) => ({ gauche: `${L} \\times ${L}`, g: [{ coef: 1, deg: 2 }], droite: `2${L}`, d: [{ coef: 2, deg: 1 }], vrai: false, code: 'double_pour_carre' }),
    somme: (L) => { const { a, b } = tirerCouple(); return { gauche: `${a}${L} + ${b}${L}`, g: [{ coef: a + b, deg: 1 }], droite: `${a + b}${L}`, d: [{ coef: a + b, deg: 1 }], vrai: true }; },
    somme_faux: (L) => { const { a, b } = tirerCouple(); const faux = randomChoice([[{ coef: a + b, deg: 2 }, 'carre_de_trop'], [{ coef: a * b, deg: 1 }, 'coefficients_multiplies']]); return { gauche: `${a}${L} + ${b}${L}`, g: [{ coef: a + b, deg: 1 }], droite: monomeTex(faux[0], L), d: [faux[0]], vrai: false, code: faux[1] }; },
    produit: (L) => { const { a, b } = tirerCouple(); return { gauche: `${a}${L} \\times ${b}${L}`, g: [{ coef: a * b, deg: 2 }], droite: `${a * b}${L}^2`, d: [{ coef: a * b, deg: 2 }], vrai: true }; },
    produit_faux: (L) => { const { a, b } = tirerCouple(); const faux = randomChoice([[{ coef: a + b, deg: 2 }, 'coefficients_additionnes'], [{ coef: a * b, deg: 1 }, 'x_oublie']]); return { gauche: `${a}${L} \\times ${b}${L}`, g: [{ coef: a * b, deg: 2 }], droite: monomeTex(faux[0], L), d: [faux[0]], vrai: false, code: faux[1] }; },
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const noms = shuffle(Object.keys(GABARITS)).slice(0, 4);
        const items = noms.map((nom) => { const g = GABARITS[nom](L); return { nom, ...g, ligne: tex(`${g.gauche} = ${g.droite}`) }; });
        const nbVraies = items.filter((it) => it.vrai).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        if (new Set(items.map((it) => it.gauche)).size < 4) continue;
        items.forEach((it) => { if (memeExpr(it.g, it.d) !== it.vrai) throw new Error(`gen_1109 : ligne mal étiquetée ${it.ligne}`); });
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, items.filter((it) => it.code).map((it) => ({ code: codeDe(it.code, it.nom === 'x_plus_x_faux' || it.nom === 'x_fois_x_faux'), ligne: it.ligne })));
        payload._meta.params = { lettre: L, items: items.map(({ nom, vrai, code }) => ({ nom, vrai, ...(code ? { code } : {}) })) };
        return payload;
    }
    const lignes = [tex('x + x = 2x'), tex('x \\times x = 2x'), tex('4x + 5x = 9x'), tex('4x \\times 5x = 9x^2')];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, [{ code: CODE_COMMUN.double_pour_carre, ligne: lignes[1] }, { code: CODE_COMMUN.coefficients_additionnes, ligne: lignes[3] }]);
    payload._meta.params = { lettre: 'x', items: [{ nom: 'x_plus_x', vrai: true }, { nom: 'x_fois_x_faux', vrai: false, code: 'double_pour_carre' }, { nom: 'somme', vrai: true }, { nom: 'produit_faux', vrai: false, code: 'coefficients_additionnes' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'la clé est une ÉCRITURE réduite ; trois écritures fausses distinctes (deux pour x × x), chacune une conception nommée ; l\'encadrement par position n\'a pas de sens sur des expressions, les choix sont mélangés (résidu à mesurer à la revue)',
        'sacrifié : le couple (3 ; 2), l\'exemple du programme, en somme et en produit, dans les deux ordres',
    ],
    qcm_unique: {
        support: '3x + 2x (l\'item DEPP 4e 2023 q.6 tel quel), 3x × 2x, x × x, x + x ; coefficients 2..9 distincts',
        familles: {
            carre_de_trop: '3x + 2x → 5x² (25,9 %), la principale ; x + x → x²',
            somme_produit: '3x + 2x → 6x² (2,4 %) ; 3x × 2x → 5x',
            coefficients_multiplies: '3x + 2x → 6x (3,5 %)',
            coefficients_additionnes: '3x × 2x → 5x²',
            x_oublie: '3x × 2x → 6x ; x + x → 2 ou x',
            double_pour_carre: 'x × x → 2x (base_fois_exposant de 1101 vue depuis la lettre)',
        },
    },
    vf_tableau_vf: 'quatre lignes parmi dix gabarits (les cinq règles justes, et leurs formes fausses), vérifiées en valeur pour x = 2, 3, 5',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    reduire_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'reduire', label: L_QCM, etapes: 1, formatsList: [{ key: 'reduire_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
